import React from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { CheckCircle2, ShieldCheck, ArrowRight } from 'lucide-react';
import { LogoMark } from '../components/LogoMark';
import { certificationsData } from '../data/certifications';
import { CertificationCard } from '../components/CertificationCard';
import { PartnerCTA } from '../components/PartnerCTA';
export function WhoWeArePage() {
  const leadershipTeam = [{
    name: 'Matthew Butterworth',
    title: 'Founder and Chief Executive Officer',
    division: null,
    image: 'https://cdn.magicpatterns.com/uploads/1RZgczWR3wsrbNRgmZRPJ8/Matthew_Butterworth_-_Chiltern_Global_Chief_Executive_Officer_2.png',
    bio: "Matthew founded Chiltern Global and serves as Chief Executive Officer. With over 30 years of police service experience and a former Senior Detective in the Metropolitan Police, he holds a master's degree in Intelligence and Security. Matthew has embedded rigorous quality control standards across the organisation and helped establish the company as a trusted partner to military, police and security forces globally."
  }, {
    name: 'Lewis Butterworth',
    title: 'Chiltern Global Managing Director',
    division: null,
    image: 'https://cdn.magicpatterns.com/uploads/5LJCgZk4TkFkdBLi8fmrSD/Lewis_Butterworth_-_Chiltern_Global_Managing_Director_1.png',
    bio: "Lewis joined Chiltern Global after completing a bachelor's degree in politics and later earned an MSc in Marketing. He shapes and executes the company's marketing strategy while also managing daily operations, IT systems and administrative functions. Lewis played a key role in supporting the company to achieve Cyber Essentials Plus certification, strengthening the organisation's digital security posture."
  }, {
    name: 'Ollie Butterworth',
    title: 'Managing Director, Chiltern Defence',
    division: 'Chiltern Defence',
    image: 'https://cdn.magicpatterns.com/uploads/t6kkfQ5SDdHDE42z7mgBwe/Ollie_Butterworth_-_Managing_Director_of_Chiktern_Defence.png',
    bio: "Ollie leads Chiltern Defence, the registered corporation in the United Arab Emirates, and is responsible for expanding the company's international presence across the Middle East. With project management experience and fluency in Mandarin, he supports global business development and strategic partnerships, working closely with manufacturing partners on product design and production for both core range and bespoke government contracts."
  }, {
    name: 'Eddy Butterworth',
    title: 'Managing Director, Medical Warehouse Global',
    division: 'Medical Warehouse Global',
    image: 'https://cdn.magicpatterns.com/uploads/oCQPzn8UJU6H8TWBEVZrNL/Eddy_Butterworth_-_Managing_Director_of_Medical_Warehouse_Global.png',
    bio: "Eddy leads Medical Warehouse Global and has contributed to the growth of specialist knowledge and production capabilities in ballistics. Holding a bachelor's degree in mechanical engineering, he progressed into project management for specialist products within the police and security markets. Eddy combines technical expertise with an innovative approach to deliver tailored solutions for front-line services."
  }, {
    name: 'Graham Chastell',
    title: 'Business Development Lead',
    division: 'Chiltern Defence',
    image: 'https://cdn.magicpatterns.com/uploads/eNjczffixQTmnt8x7rm2Xr/Graham_Chastell.png',
    bio: 'A retired British Army Sergeant Major with over 22 years of military service, Graham leads business and product development at Chiltern Global. His operational background spans air defence, CBRN, riot control, medical training and infantry tactics. He applies this front-line expertise directly to product design and manufacturing innovation, ensuring equipment meets the practical demands of defence and police personnel.'
  }, {
    name: 'Sophia Yang',
    title: 'Procurement and Supply Chain Manager',
    division: null,
    image: 'https://cdn.magicpatterns.com/uploads/kPJoYsBoJGEG7YMrz7MsNa/Sophia_Yang.png',
    bio: 'Sophia joined Chiltern Global in 2011 and manages purchasing operations across the Far East, bringing over a decade of procurement and supply chain expertise. She is responsible for sourcing and quality assurance of materials and products for both core range and bespoke government contracts, maintaining strong relationships with local manufacturers to support cost-effective, high-quality supply solutions.'
  }, {
    name: 'Cameron McDade',
    title: 'Sales Manager',
    division: null,
    image: 'https://cdn.magicpatterns.com/uploads/aJpt3L9fGJNRogYq3VHw8o/Cameron_McDade.png',
    bio: 'Cameron has served as Sales Manager since 2022, with a focus on the police and prisons sector. He has helped secure framework agreements including YPO and BlueLight Commercial, and supported Chiltern Global in becoming the preferred supplier of the National Uniform Managed Service. With 26 years in the UK prison service and a further 8 years supplying law enforcement agencies, he brings deep procurement insight to front-line service delivery.'
  }];
  const history = [{
    year: '2005',
    title: 'Company Established',
    desc: 'Chiltern Global founded as a specialist supplier of equipment for the defence and security sectors, built on a foundation of law enforcement expertise and a commitment to quality in safety-critical products.'
  }, {
    year: '2011',
    title: 'Expanded Manufacturing Capacity',
    desc: 'Scaled operational capability and strengthened supply chain partnerships to support a growing range of manufacturing and procurement requirements across multiple product categories.'
  }, {
    year: '2012\u20132016',
    title: 'Framework Appointments',
    desc: 'Appointed to key public sector procurement frameworks including BlueLight Commercial and YPO, providing compliant and efficient routes to market for emergency services and government buyers.'
  }, {
    year: '2017\u20132021',
    title: 'International Expansion',
    desc: 'Established Chiltern Defence in the United Arab Emirates, extending international operations and logistics capability to support defence and security clients across the Middle East.'
  }, {
    year: '2022',
    title: 'Expanded Commercial Capacity',
    desc: 'Strengthened market reach and broadened sector engagement, deepening coverage across police, prisons and custodial services alongside existing defence and medical operations.'
  }, {
    year: 'Present',
    title: 'Multi-Sector Manufacturing Partner',
    desc: 'Operating across defence, police, prisons and medical sectors with offices in the United Kingdom and the United Arab Emirates, delivering specification-led equipment through structured procurement and quality-assured manufacturing.'
  }];
  const values = [{
    title: 'Integrity',
    definition: 'How we deliver value across every stage of defence and security manufacturing.',
    description: 'At Chiltern Global, integrity is not just a word but how we deliver value across every stage of defence and security manufacturing. Our commitment to acting with integrity is proven through the standards we uphold in procurement, production and client engagement.',
    points: ['Ethical conduct in every relationship: from procurement and manufacturing to client engagement, we maintain high standards of transparency and accountability', 'Quality and compliance: our multiple ISO certifications including ISO 9001:2015, ISO 14001:2015 and JOSCAR support structured processes that ensure consistency and product reliability', 'Cyber security in sensitive environments: Chiltern Global are proud holders of the Cyber Essentials Plus certificate, the UK government-backed cyber security standard', 'Continuous review and adaptation: we regularly assess our internal processes and systems to ensure we operate at the highest standard']
  }, {
    title: 'Reliability',
    definition: 'A trusted manufacturing partner in defence, policing and security for over 20 years.',
    description: 'At Chiltern Global, we pride ourselves on being a reliable manufacturing partner in the defence, policing and security sectors for over 20 years. Our clients trust us to deliver products that meet rigorous specifications, whether for individual bespoke contracts or high-volume orders.',
    points: ['End-to-end project management: we utilise a bespoke project management system that allows full visibility and control at every phase, from design through to delivery', 'Strict supervision of manufacturing: we manage every step of production to ensure we maintain high standards of quality, consistency and efficiency', 'Commitment to quality: whether for individual bespoke contracts or high-volume orders, our clients trust us to deliver products that meet rigorous defence and security specifications', 'Consistency under pressure: our processes ensure we deliver high quality, even with short deadlines and strict requirements']
  }, {
    title: 'Innovation',
    definition: 'Anticipating challenges, adopting emerging technologies and driving industry change.',
    description: 'Within the fast-moving sectors of defence, policing and security, innovation is not optional but essential. At Chiltern Global, we build relationships with clients to anticipate challenges, adopt emerging technologies and drive industry change.',
    points: ['Client-led innovation: we work closely with defence and police agencies to understand evolving operational needs, translating them into advanced specifications and solutions', 'Structured research, development and prototyping: our research and development processes explore new materials, systems and manufacturing techniques to deliver advanced and bespoke products', 'Adaptation to trends: from smart textiles to ballistic enhancements, we stay ahead of industry shifts', 'Innovation and quality combined: every concept is validated through our ISO-certified processes to ensure it meets the highest standards']
  }];
  return <>
      <PageHero label="About Us" title="Who We Are" subtitle="Meet the team behind Chiltern Global. Our people bring together operational, procurement and manufacturing expertise to deliver for front-line services." backgroundImage="https://cdn.magicpatterns.com/uploads/sCTeZG5Mo7gieHgmWAmYb6/Who_we_are.jpg" data-id="element-1566" />

      <Breadcrumbs items={[{
      label: 'About Us',
      to: '/about'
    }, {
      label: 'Who We Are'
    }]} data-id="element-1567" />

      {/* SECTION 1: Our People */}
      <section className="bg-white py-24" data-id="element-1568">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1569">
          <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-2 block" data-id="element-1570">
            Our People
          </span>
          <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-1571">
            Leadership and Team
          </h2>
          <p className="text-lg text-steel-600 max-w-3xl leading-relaxed mb-12" data-id="element-1572">
            Our leadership team brings together operational, procurement and
            technical expertise spanning military service, law enforcement,
            engineering and international supply chain management. This breadth
            of experience ensures that every product is designed, sourced and
            delivered to meet the exacting standards of front-line environments.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1573">
            {leadershipTeam.map((person, index) => {
            const isOrange = index % 2 !== 0;
            const strokeColor = isOrange ? '#FFFFFF' : '#A84A14';
            return <div key={index} className="bg-white border border-steel-200 rounded-sm overflow-hidden hover:border-cg-orange-600 transition-colors group flex flex-col" data-id="element-1574">
                  {/* Portrait Area */}
                  <div className={`relative flex items-center justify-center h-72 overflow-hidden ${isOrange ? 'bg-cg-orange-600' : 'bg-slate-100'}`} data-id="element-1575">
                    {/* Tactical topographical map pattern */}
                    <svg className={`absolute inset-0 w-full h-full ${isOrange ? 'opacity-[0.15]' : 'opacity-[0.18]'}`} viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" data-id="element-1576">
                      {/* Grid reference lines */}
                      <line x1="0" y1="50" x2="400" y2="50" stroke={strokeColor} strokeWidth="0.8" data-id="element-1577" />
                      <line x1="0" y1="100" x2="400" y2="100" stroke={strokeColor} strokeWidth="0.8" data-id="element-1578" />
                      <line x1="0" y1="150" x2="400" y2="150" stroke={strokeColor} strokeWidth="0.8" data-id="element-1579" />
                      <line x1="0" y1="200" x2="400" y2="200" stroke={strokeColor} strokeWidth="0.8" data-id="element-1580" />
                      <line x1="0" y1="250" x2="400" y2="250" stroke={strokeColor} strokeWidth="0.8" data-id="element-1581" />
                      <line x1="50" y1="0" x2="50" y2="300" stroke={strokeColor} strokeWidth="0.8" data-id="element-1582" />
                      <line x1="100" y1="0" x2="100" y2="300" stroke={strokeColor} strokeWidth="0.8" data-id="element-1583" />
                      <line x1="150" y1="0" x2="150" y2="300" stroke={strokeColor} strokeWidth="0.8" data-id="element-1584" />
                      <line x1="200" y1="0" x2="200" y2="300" stroke={strokeColor} strokeWidth="0.8" data-id="element-1585" />
                      <line x1="250" y1="0" x2="250" y2="300" stroke={strokeColor} strokeWidth="0.8" data-id="element-1586" />
                      <line x1="300" y1="0" x2="300" y2="300" stroke={strokeColor} strokeWidth="0.8" data-id="element-1587" />
                      <line x1="350" y1="0" x2="350" y2="300" stroke={strokeColor} strokeWidth="0.8" data-id="element-1588" />

                      {/* Contour lines — primary elevation */}
                      <path d="M20 180 Q60 140 110 155 Q160 170 200 130 Q240 90 290 110 Q340 130 380 100" stroke={strokeColor} strokeWidth="2.5" fill="none" data-id="element-1589" />
                      <path d="M10 210 Q50 175 100 185 Q155 195 200 160 Q250 125 300 140 Q350 155 395 125" stroke={strokeColor} strokeWidth="1.8" fill="none" data-id="element-1590" />
                      <path d="M5 240 Q45 210 95 215 Q150 220 200 190 Q255 160 310 170 Q360 180 400 155" stroke={strokeColor} strokeWidth="1.8" fill="none" data-id="element-1591" />

                      {/* Contour lines — secondary elevation cluster */}
                      <path d="M30 120 Q70 95 120 105 Q170 115 210 85 Q250 55 295 70 Q340 85 385 60" stroke={strokeColor} strokeWidth="2" fill="none" data-id="element-1592" />
                      <path d="M25 90 Q65 70 115 75 Q165 80 205 55 Q245 30 290 42 Q335 55 380 35" stroke={strokeColor} strokeWidth="1.4" fill="none" data-id="element-1593" />

                      {/* Contour lines — lower terrain */}
                      <path d="M15 260 Q55 240 105 248 Q160 255 205 228 Q255 200 310 212 Q360 225 400 205" stroke={strokeColor} strokeWidth="1.4" fill="none" data-id="element-1594" />

                      {/* Closed contour — hilltop feature */}
                      <ellipse cx="140" cy="140" rx="35" ry="20" stroke={strokeColor} strokeWidth="2" fill="none" transform="rotate(-15 140 140)" data-id="element-1595" />
                      <ellipse cx="140" cy="140" rx="20" ry="11" stroke={strokeColor} strokeWidth="1.4" fill="none" transform="rotate(-15 140 140)" data-id="element-1596" />

                      {/* Closed contour — second hilltop */}
                      <ellipse cx="310" cy="90" rx="28" ry="16" stroke={strokeColor} strokeWidth="2" fill="none" transform="rotate(10 310 90)" data-id="element-1597" />
                      <ellipse cx="310" cy="90" rx="14" ry="8" stroke={strokeColor} strokeWidth="1.4" fill="none" transform="rotate(10 310 90)" data-id="element-1598" />

                      {/* Depression contour (hachure marks) */}
                      <ellipse cx="260" cy="210" rx="22" ry="14" stroke={strokeColor} strokeWidth="1.6" fill="none" data-id="element-1599" />
                      <line x1="242" y1="203" x2="245" y2="207" stroke={strokeColor} strokeWidth="1.2" data-id="element-1600" />
                      <line x1="250" y1="198" x2="252" y2="202" stroke={strokeColor} strokeWidth="1.2" data-id="element-1601" />
                      <line x1="260" y1="196" x2="260" y2="200" stroke={strokeColor} strokeWidth="1.2" data-id="element-1602" />
                      <line x1="270" y1="198" x2="268" y2="202" stroke={strokeColor} strokeWidth="1.2" data-id="element-1603" />
                      <line x1="278" y1="203" x2="275" y2="207" stroke={strokeColor} strokeWidth="1.2" data-id="element-1604" />

                      {/* Grid reference tick marks along edges */}
                      <line x1="0" y1="48" x2="10" y2="48" stroke={strokeColor} strokeWidth="1.5" data-id="element-1605" />
                      <line x1="0" y1="98" x2="10" y2="98" stroke={strokeColor} strokeWidth="1.5" data-id="element-1606" />
                      <line x1="0" y1="148" x2="10" y2="148" stroke={strokeColor} strokeWidth="1.5" data-id="element-1607" />
                      <line x1="0" y1="198" x2="10" y2="198" stroke={strokeColor} strokeWidth="1.5" data-id="element-1608" />
                      <line x1="0" y1="248" x2="10" y2="248" stroke={strokeColor} strokeWidth="1.5" data-id="element-1609" />
                      <line x1="48" y1="0" x2="48" y2="10" stroke={strokeColor} strokeWidth="1.5" data-id="element-1610" />
                      <line x1="98" y1="0" x2="98" y2="10" stroke={strokeColor} strokeWidth="1.5" data-id="element-1611" />
                      <line x1="148" y1="0" x2="148" y2="10" stroke={strokeColor} strokeWidth="1.5" data-id="element-1612" />
                      <line x1="198" y1="0" x2="198" y2="10" stroke={strokeColor} strokeWidth="1.5" data-id="element-1613" />
                      <line x1="248" y1="0" x2="248" y2="10" stroke={strokeColor} strokeWidth="1.5" data-id="element-1614" />
                      <line x1="298" y1="0" x2="298" y2="10" stroke={strokeColor} strokeWidth="1.5" data-id="element-1615" />
                      <line x1="348" y1="0" x2="348" y2="10" stroke={strokeColor} strokeWidth="1.5" data-id="element-1616" />

                      {/* Elevation markers */}
                      <text x="135" y="143" fill={strokeColor} fontSize="8" fontFamily="monospace" fontWeight="bold" data-id="element-1617">
                        ▲ 342
                      </text>
                      <text x="305" y="93" fill={strokeColor} fontSize="8" fontFamily="monospace" fontWeight="bold" data-id="element-1618">
                        ▲ 285
                      </text>
                      <text x="252" y="214" fill={strokeColor} fontSize="7" fontFamily="monospace" data-id="element-1619">
                        178
                      </text>

                      {/* Grid reference numbers */}
                      <text x="2" y="46" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1620">
                        10
                      </text>
                      <text x="2" y="96" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1621">
                        20
                      </text>
                      <text x="2" y="146" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1622">
                        30
                      </text>
                      <text x="2" y="196" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1623">
                        40
                      </text>
                      <text x="2" y="246" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1624">
                        50
                      </text>
                      <text x="47" y="8" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1625">
                        10
                      </text>
                      <text x="97" y="8" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1626">
                        20
                      </text>
                      <text x="147" y="8" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1627">
                        30
                      </text>
                      <text x="197" y="8" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1628">
                        40
                      </text>
                      <text x="247" y="8" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1629">
                        50
                      </text>
                      <text x="297" y="8" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1630">
                        60
                      </text>
                      <text x="347" y="8" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1631">
                        70
                      </text>

                      {/* Stream/water feature (dashed) */}
                      <path d="M80 280 Q100 250 130 260 Q160 270 180 240 Q200 210 230 220 Q260 230 280 200" stroke={strokeColor} strokeWidth="1.4" fill="none" strokeDasharray="6 4" data-id="element-1632" />

                      {/* Road/track feature */}
                      <path d="M350 280 Q340 250 345 220 Q350 190 360 160" stroke={strokeColor} strokeWidth="2" fill="none" strokeDasharray="8 3" data-id="element-1633" />

                      {/* Spot heights */}
                      <circle cx="80" cy="170" r="2.5" fill={strokeColor} data-id="element-1634" />
                      <text x="86" y="174" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1635">
                        214
                      </text>
                      <circle cx="340" cy="150" r="2.5" fill={strokeColor} data-id="element-1636" />
                      <text x="346" y="154" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1637">
                        196
                      </text>
                      <circle cx="200" cy="260" r="2.5" fill={strokeColor} data-id="element-1638" />
                      <text x="206" y="264" fill={strokeColor} fontSize="7" fontFamily="monospace" fontWeight="bold" data-id="element-1639">
                        122
                      </text>
                    </svg>
                    {person.image ? <img src={person.image} alt={person.name} className="relative z-10 w-full h-full object-contain object-center" data-id="element-1640" /> : <div className="relative z-10 w-24 h-24 bg-navy-900 rounded-full flex items-center justify-center text-2xl font-bold text-white" data-id="element-1641">
                        {person.name.split(' ').map(n => n[0]).join('')}
                      </div>}
                  </div>

                  {/* Content Area */}
                  <div className="p-6 flex flex-col flex-1" data-id="element-1642">
                    <h3 className="font-bold text-navy-900 text-lg mb-1" data-id="element-1643">
                      {person.name}
                    </h3>
                    <span className={`text-xs font-bold text-cg-orange-600 uppercase tracking-wider block ${person.division ? 'mb-1' : 'mb-4'}`} data-id="element-1644">
                      {person.title}
                    </span>
                    {person.division && <span className="text-[10px] font-bold text-steel-400 uppercase tracking-wider block mb-4" data-id="element-1645">
                        {person.division}
                      </span>}
                    <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1646">
                      {person.bio}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* SECTION: Our Divisions */}
      <section className="bg-white py-20 border-t border-steel-100" data-id="element-1647">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1648">
          <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-2 block" data-id="element-1649">
            Our Group
          </span>
          <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-1650">
            Our Divisions
          </h2>
          <p className="text-steel-600 max-w-3xl mb-12" data-id="element-1651">
            Chiltern Global operates through three specialist divisions, each
            focused on distinct markets and capabilities within defence,
            security and medical sectors.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1652">
            <div className="bg-slate-50 border border-steel-200 border-t-4 border-t-cg-orange-600 p-8 rounded-sm" data-id="element-1653">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1654">
                Chiltern Global
              </h3>
              <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider block mb-4" data-id="element-1655">
                Core Operations
              </span>
              <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1656">
                The parent company and primary manufacturing partner, supplying
                specialist equipment to defence, police, prisons and medical
                sectors from our United Kingdom headquarters.
              </p>
            </div>

            <div className="bg-slate-50 border border-steel-200 border-t-4 border-t-navy-900 p-8 rounded-sm" data-id="element-1657">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1658">
                Chiltern Defence
              </h3>
              <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider block mb-4" data-id="element-1659">
                International Defence
              </span>
              <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1660">
                Our registered corporation in the United Arab Emirates,
                responsible for international defence operations, business
                development and specialist product design for military
                requirements.
              </p>
            </div>

            <div className="bg-slate-50 border border-steel-200 border-t-4 border-t-steel-400 p-8 rounded-sm" data-id="element-1661">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1662">
                Medical Warehouse Global
              </h3>
              <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider block mb-4" data-id="element-1663">
                Medical and Ballistics
              </span>
              <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1664">
                Our specialist medical division, focused on textile-based
                medical equipment, ballistic product development and tailored
                solutions for healthcare and emergency services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: Certifications & Accreditations */}
      <section className="bg-white py-20 border-t border-steel-200" data-id="element-1665">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1666">
          <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-2 block" data-id="element-1667">
            Our Credentials
          </span>
          <h2 className="text-3xl font-bold text-navy-900 mb-12" data-id="element-1668">
            Certifications & Accreditations
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1669">
            {certificationsData.map(card => <CertificationCard key={card.id} card={card} data-id="element-1670" />)}
          </div>
        </div>
      </section>

      {/* SECTION 2: Our History */}
      <section className="bg-slate-50 py-24 border-t border-steel-200" data-id="element-1671">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1672">
          <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-1673">Our History</h2>
          <p className="text-steel-600 mb-12 max-w-3xl" data-id="element-1674">
            Chiltern Global was founded in 2005 on a strong foundation of law
            enforcement and operational expertise. From a specialist defence
            supplier to a recognised manufacturing partner across multiple
            sectors, the company has grown by bringing together professionals
            with deep experience in military service, custodial environments,
            policing and public safety, ensuring that real-world operational
            insight drives every decision.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1675">
            {history.map(item => <div key={item.year} className="relative pl-8 border-l-2 border-steel-200" data-id="element-1676">
                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-cg-orange-600 border-4 border-slate-50" data-id="element-1677" />
                <span className="text-sm font-bold text-cg-orange-600 uppercase tracking-wider block mb-2" data-id="element-1678">
                  {item.year}
                </span>
                <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1679">
                  {item.title}
                </h3>
                <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1680">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: Our Values */}
      <section className="bg-navy-900 py-24 text-white" data-id="element-1681">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1682">
          <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-2 block" data-id="element-1683">
            Our Principles
          </span>
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1684">Our Values</h2>
          <p className="text-steel-300 max-w-3xl mb-16" data-id="element-1685">
            Three principles guide every decision we make, from sourcing and
            production through to delivery and after-sales support.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1686">
            {values.map(value => <div key={value.title} className="bg-navy-800 border border-white/10 p-8 rounded-sm flex flex-col" data-id="element-1687">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1688">
                  {value.title}
                </h3>
                <p className="text-cg-orange-600 font-semibold text-sm mb-6" data-id="element-1689">
                  {value.definition}
                </p>
                <p className="text-steel-300 mb-8 leading-relaxed" data-id="element-1690">
                  {value.description}
                </p>

                <h4 className="text-xs font-bold text-white uppercase tracking-wider mb-4" data-id="element-1691">
                  How this shows up in practice
                </h4>
                <ul className="space-y-3" data-id="element-1692">
                  {value.points.map((point, i) => <li key={i} className="flex items-start gap-2 text-sm text-steel-400" data-id="element-1693">
                      <CheckCircle2 className="w-4 h-4 text-cg-orange-600 flex-shrink-0 mt-0.5" data-id="element-1694" />
                      <span data-id="element-1695">{point}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4: CTA */}
      <PartnerCTA data-id="element-1696" />
    </>;
}